define((require) => {
    "use strict";

    const Configuration     = require("shared/services/Configuration");
    const ImprovedModel     = require("shared/zbone/ImprovedModel");
    const UUIDGenerator     = require("ui/experience/widgets/UUIDGenerator");
    const ImageSlicer       = require("util/ImageSlicer").ImageSlicer;
    const EmailRenderer     = require("ui/editor/email/renderer/EmailRenderer");
    const Promise           = require("shared/util/Promise");
    const $                 = require("jquery");
    const Storage           = require("shared/services/Storage");

    // It is known Gmail restriction for email size
    const MAX_GMAIL_ALLOWED_SIZE = 102; // (Kb)

    /**
     * View model for the experience slicing.
     *
     * @class EmailSliceModel
     * @constructor
     */
    const EmailSliceModel = ImprovedModel.extend("EmailSliceModel", {

        _experience: null,
        _viewModel: null,

        url() {
            const experience = this.get("_experience");
            const pushedAtEpochTimeMs = experience.get("pushedAt");
            const pushedAtEpochTime = (pushedAtEpochTimeMs / 1000).toFixed(0);
            const experienceId = experience.get("id");
            const experienceWidth = experience.get("width");
            const experienceHeight = experience.get("height");
            const traceUUID = UUIDGenerator.createUUID();

            const constructedUrl = new URL(Configuration.experienceUrl2PngBaseUrl);
            constructedUrl.search = new URLSearchParams([
                ["experienceId", experienceId],
                ["pushedAtEpochTime", pushedAtEpochTime],
                ["experienceWidth", experienceWidth],
                ["experienceHeight", experienceHeight],
                ["quality", "auto:best"],
                ["url2PngSettings", `viewport=${experienceWidth}x${experienceHeight}|fullpage=false`],
                ["traceUUID", traceUUID]
            ]).toString();
            return constructedUrl.href;
        },

        /**
         * Call original Backbone.Model#fetch with `dataType` equal `text` for $.ajax.
         * This is a stack exchange special for dealing with raw text return objects.
         * https://stackoverflow.com/questions/22567027/backbone-model-is-plain-text-file-how-to-fetch
         *
         * Also after we get screenshot we need to preload it somehow to be able use "cropped" version of this URL
         * F.E. we got url like:
         * https://thumbnail.creator-stage.zmags.com/.../c_fill,g_north,h_400,w_500/v1612258825/https://cnc-api-dev....
         * But before we can use cropped version:
         * https://thumbnail.creator-stage.zmags.com/c_fill,g_north,h_400,w_500/c_crop,h_31,w_83,x_304,y_165/...
         * we need to preload original image, otherwise - 401 error. It is because Cloudinary specification.
         *
         * So, created own promise which resolved after we load(preload) original URL
         */
        fetch(options) {
            // eslint-disable-next-line no-undef
            options = _.extend(options || {}, {
                dataType: "text"
            });
            const viewModel = this.get("_viewModel");
            viewModel.set({isProcessingEmail: true});
            return this.constructor.__super__.fetch.call(this, options).then((model) => {
                const screenshotURL = model.get("screenshotURL");
                const loadImage = (deferred) => {
                    const image = new Image();
                    const loaded = () => {
                        const tableJSON = this._generateJson(screenshotURL, options.widgets);
                        const tableHTML = this._generateHtml(screenshotURL, tableJSON, options.espName, options.userHTML);
                        let sizeCalcHTML = tableHTML;
                        if (options.espName === "klaviyo" || options.espName === "sfmc") {
                            sizeCalcHTML = sizeCalcHTML.html_code;
                        }
                        const sizeWarning = this._kByteSize(sizeCalcHTML) >= MAX_GMAIL_ALLOWED_SIZE;
                        model.set({
                            tableJSON, tableHTML, sizeWarning, emailSize: this._kByteSize(sizeCalcHTML)
                        });
                        model.set({tableJSON, tableHTML, sizeWarning});
                        deferred.resolve(model);
                    };
                    const errored = () => {
                        deferred.reject();
                    };
                    deferred.always(()=>{
                        image.onload = null;
                        image.onerror = null;
                        image.onabort = null;
                        viewModel.set({isProcessingEmail: false});
                    });
                    image.onload = loaded;
                    image.onerror = errored;
                    image.onabort = errored;
                    image.src = screenshotURL;
                };
                return Promise.deferred(loadImage);
            }).fail((e) => Promise.reject(e));
        },

        parse(screenshotURL) {
            return {screenshotURL};
        },

        _kByteSize(str) {
            // Get kilobytes. In decimal, not in binary
            return new Blob([str]).size / 1000;
        },

        _generateHtml(screenshotURL, tableJSON, espName = "default", userHTML = "default") {
            const experience = this.get("_experience");
            if (espName === "ometria") {
                const token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token");
                const bearerToken = "Bearer " + token;
                const payload = {
                    screenshotURL,
                    tableJSON,
                    width: experience.get("width"),
                    height: experience.get("height")
                };
                const OMETRIA_API_ENDPOINT = Configuration.ometriaApiUrl;
                const options = {
                    type: "POST",
                    url: OMETRIA_API_ENDPOINT,
                    headers: {
                        "authorization": bearerToken
                    },
                    data: JSON.stringify(payload),
                    success: "success",
                    dataType: "json",
                    async: false
                };
                const requestTableHTML = $.ajax(options);
                const response = requestTableHTML.responseJSON || null;
                const minifiedHTML = response ? response.html_code : null;
                this._pasteCodeToHiddenTextarea(minifiedHTML);
                return minifiedHTML;
            } else if (espName === "klaviyo") {
                const token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token");
                const bearerToken = "Bearer " + token;
                const payload = {
                    tableHelper: {
                        screenshotURL,
                        tableJSON,
                        width: experience.get("width"),
                        height: experience.get("height")
                    },
                    userHTML: userHTML
                };
                const KLAVIYO_API_ENDPOINT = Configuration.klaviyoApiUrl;
                const options = {
                    type: "POST",
                    url: KLAVIYO_API_ENDPOINT,
                    headers: {
                        "authorization": bearerToken
                    },
                    data: JSON.stringify(payload),
                    success: "success",
                    dataType: "json",
                    async: false
                };
                const requestTableHTML = $.ajax(options);
                const minifiedHTML = requestTableHTML.responseJSON || null;
                this._pasteCodeToHiddenTextarea(minifiedHTML);
                return minifiedHTML;
            } else if (espName === "sfmc") {
                const token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token");
                const bearerToken = "Bearer " + token;
                const payload = {
                    tableHelper: {
                        screenshotURL,
                        tableJSON,
                        width: experience.get("width"),
                        height: experience.get("height")
                    },
                    userHTML: userHTML
                };
                const SFMC_API_ENDPOINT = Configuration.sfmcApiUrl;
                const options = {
                    type: "POST",
                    url: SFMC_API_ENDPOINT,
                    headers: {
                        "authorization": bearerToken
                    },
                    data: JSON.stringify(payload),
                    success: "success",
                    dataType: "json",
                    async: false
                };
                const requestTableHTML = $.ajax(options);
                const minifiedHTML = requestTableHTML.responseJSON || null;
                this._pasteCodeToHiddenTextarea(minifiedHTML);
                return minifiedHTML;
            }

            const tableHTML = new EmailRenderer({
                screenshotURL,
                tableJSON,
                width: experience.get("width"),
                height: experience.get("height")
            }).render();
            const minifiedHTML = `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">${this._minifyCode(tableHTML.el.outerHTML)}`;
            this._pasteCodeToHiddenTextarea(minifiedHTML);
            return minifiedHTML;
        },

        _minifyCode(code) {
            return String(code)
                .replace(/>[\r\n ]+</g, "><")
                .replace(/(<.*?>)|\s+/g, (m, $1) => $1 || " ")
                .trim();
        },
        /**
         * Need this for QA purposes
         * @param code
         * @private
         */
        _pasteCodeToHiddenTextarea(code) {
            let input = document.getElementById("emailHtmlSourceCode");
            if (!input) {
                input = document.createElement("TEXTAREA");
                input.setAttribute("id", "emailHtmlSourceCode");
                input.setAttribute("style", "position:absolute; bottom:0; left: -9999px; max-width:300px;");
                document.getElementsByTagName("body")[0].appendChild(input);
            }
            input.value = code;
        },

        _generateJson(screenshotURL, widgets) {
            const experience = this.get("_experience");
            const imageSlicer = ImageSlicer(widgets, experience.get("height"), experience.get("width"), true);
            const tableJSON = imageSlicer.slice();
            tableJSON.forEach((tr) => {
                tr.forEach((td) => {
                    const {
                        top, left, width, height, isSpacer
                    } = td;
                    if (!isSpacer) {
                        td.img = this._crop({
                            top, left, width, height, originalURL: screenshotURL
                        });
                    }
                });
            });
            return tableJSON;
        },

        _crop({
            top, left, width, height, originalURL
        }) {
            const url = new URL(originalURL);
            const [startString, type, method, signature, fillParams, ...rest] = url.pathname.split("/");
            const cropParams = `c_crop,x_${left},y_${top},w_${width},h_${height}`;
            url.pathname = [startString, type, method, signature, fillParams, cropParams, ...rest].join("/");
            return url.href;
        },

        defaults: {
            screenshotURL: null,
            tableJSON: [],
            tableHTML: null,
            sizeWarning: false
        }
    });

    return EmailSliceModel;
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/email/EmailSliceModel.js